import { translateText } from '~/common/components/TranslatedLabel/TranslatedLabel';

import { UserSupportedLocale } from '../models/common.model';

/**
 * Legal URLs are built from `globalThis.appConfig.legalTermsBaseUrl` (per-environment
 * CDN host) plus document paths selected by rebrand feature flags. Host changes in
 * lower envs do not imply a flag flip; legacy and GM Fleet PDFs are mirrored on the
 * new host where `legalTermsBaseUrl` was updated.
 */
export type LegalTermConfig = {
  key: string;
  title: string;
  /**
   * The day the document was published, as `yyyy-MM-dd`. A publication date names a day rather
   * than a moment, so it is held in the shape a reader's field order can be applied to instead of
   * in one reader's layout.
   */
  lastUpdated: string;
  link: string;
};

const ONSTAR_FLEET_INTELLIGENCE_USER_TERMS_PATH =
  '/user-terms/User_Terms_OnStar_Fleet_Intelligence_07012026.pdf';
const ONSTAR_FLEET_INTELLIGENCE_PRIVACY_PATH =
  '/privacy-statements/Privacy_Statement_OnStar_Fleet_Intelligence_07012026.pdf';

const buildLocaleLegalLinks = (url: string) => ({
  [UserSupportedLocale.en_US]: url,
  [UserSupportedLocale.en_CA]: url,
  [UserSupportedLocale.fr_CA]: url,
});

const buildEncodedLegalUrl = (path: string) =>
  encodeURI(`${globalThis.appConfig.legalTermsBaseUrl}${path}`);

const LEGACY_USER_TERMS_URL = buildEncodedLegalUrl(
  '/2025-04-23/GM Envolve User Terms.pdf'
);
const UPDATED_USER_TERMS_URL = buildEncodedLegalUrl(
  '/2026-05-28/GM_Fleet_User_Terms.pdf'
);
const LEGACY_PRIVACY_URL = buildEncodedLegalUrl(
  '/2025-04-23/GM Envolve Privacy Statement.pdf'
);
const UPDATED_PRIVACY_URL = buildEncodedLegalUrl(
  '/2026-06-01/GMFleet_Platform_Privacy_Statement-2026-06-01.pdf'
);
const UPDATED_DRIVE_BLOCK_TERMS_URL = `${globalThis.appConfig.legalTermsBaseUrl}/drive-block/CSP_Drive_Block_terms_052726.pdf`;
const LEGACY_DRIVE_BLOCK_TERMS_URL = `${globalThis.appConfig.legalTermsBaseUrl}/drive-block/CSP_Drive_Block_GA_Terms_and_Conditions_02112026.pdf`;
const DUALCAM_TERMS_URL = buildEncodedLegalUrl('/2026-07-21/dualcam-terms.pdf');

const buildDualCamTerm = (): LegalTermConfig => ({
  key: 'dualcam-terms',
  title: translateText('account:organization.legal.dualCamTerms'),
  lastUpdated: '2026-07-21',
  link: DUALCAM_TERMS_URL,
});

const buildOnStarFleetIntelligenceUserTermsLinks = () =>
  buildLocaleLegalLinks(
    buildEncodedLegalUrl(ONSTAR_FLEET_INTELLIGENCE_USER_TERMS_PATH)
  );

const buildOnStarFleetIntelligencePrivacyLinks = () =>
  buildLocaleLegalLinks(
    buildEncodedLegalUrl(ONSTAR_FLEET_INTELLIGENCE_PRIVACY_PATH)
  );

const buildOnStarFleetIntelligenceLegalPageTerms = (): LegalTermConfig[] => [
  {
    key: 'user-terms',
    title: translateText('account:organization.legal.userTerms'),
    lastUpdated: '2026-07-01',
    link: buildEncodedLegalUrl(ONSTAR_FLEET_INTELLIGENCE_USER_TERMS_PATH),
  },
  {
    key: 'rve-d-beta-terms',
    title: translateText('account:organization.legal.driveBlockTerms'),
    lastUpdated: '2026-05-27',
    link: UPDATED_DRIVE_BLOCK_TERMS_URL,
  },
  {
    key: 'privacy-statement',
    title: translateText('account:organization.legal.privacyStatement'),
    lastUpdated: '2026-07-01',
    link: buildEncodedLegalUrl(ONSTAR_FLEET_INTELLIGENCE_PRIVACY_PATH),
  },
  buildDualCamTerm(),
];

export const USER_TERMS_LINKS_OLD = buildLocaleLegalLinks(
  LEGACY_USER_TERMS_URL
);

export const USER_TERMS_LINKS_UPDATED = buildLocaleLegalLinks(
  UPDATED_USER_TERMS_URL
);

export const USER_PRIVACY_LINKS_OLD = buildLocaleLegalLinks(LEGACY_PRIVACY_URL);

export const USER_PRIVACY_LINKS_UPDATED =
  buildLocaleLegalLinks(UPDATED_PRIVACY_URL);

export const LEGAL_PAGE_TERMS_UPDATED: LegalTermConfig[] = [
  {
    key: 'user-terms',
    title: translateText('account:organization.legal.userTerms'),
    lastUpdated: '2026-05-28',
    link: UPDATED_USER_TERMS_URL,
  },
  {
    key: 'rve-d-beta-terms',
    title: translateText('account:organization.legal.driveBlockTerms'),
    lastUpdated: '2026-05-27',
    link: UPDATED_DRIVE_BLOCK_TERMS_URL,
  },
  {
    key: 'privacy-statement',
    title: translateText('account:organization.legal.privacyStatement'),
    lastUpdated: '2026-06-01',
    link: UPDATED_PRIVACY_URL,
  },
  buildDualCamTerm(),
];

export const LEGAL_PAGE_TERMS_OLD: LegalTermConfig[] = [
  {
    key: 'user-terms',
    title: translateText('account:organization.legal.userTerms'),
    lastUpdated: '2025-04-23',
    link: LEGACY_USER_TERMS_URL,
  },
  {
    key: 'rve-d-beta-terms',
    title: translateText('account:organization.legal.driveBlockTerms'),
    lastUpdated: '2026-02-11',
    link: LEGACY_DRIVE_BLOCK_TERMS_URL,
  },
  {
    key: 'privacy-statement',
    title: translateText('account:organization.legal.privacyStatement'),
    lastUpdated: '2025-04-23',
    link: LEGACY_PRIVACY_URL,
  },
  buildDualCamTerm(),
];

/**
 * Returns legal page terms for the account legal view and related links.
 * @param isGmFleetRebrandEnabled - When true, returns GM Fleet rebrand documents
 * @param isOnStarFleetIntelligenceRebrandEnabled - When true, returns OnStar Fleet Intelligence documents
 */
export const getLegalPageTerms = (
  isGmFleetRebrandEnabled: boolean,
  isOnStarFleetIntelligenceRebrandEnabled = false
): LegalTermConfig[] => {
  if (isOnStarFleetIntelligenceRebrandEnabled) {
    return buildOnStarFleetIntelligenceLegalPageTerms();
  }

  return isGmFleetRebrandEnabled
    ? LEGAL_PAGE_TERMS_UPDATED
    : LEGAL_PAGE_TERMS_OLD;
};

/**
 * Returns user terms PDF links for the /user-terms page.
 * @param isGmFleetRebrandEnabled - When true, returns GM Fleet rebrand documents
 * @param isOnStarFleetIntelligenceRebrandEnabled - When true, returns OnStar Fleet Intelligence documents
 */
export const getUserTermsLinks = (
  isGmFleetRebrandEnabled: boolean,
  isOnStarFleetIntelligenceRebrandEnabled = false
) => {
  if (isOnStarFleetIntelligenceRebrandEnabled) {
    return buildOnStarFleetIntelligenceUserTermsLinks();
  }

  return isGmFleetRebrandEnabled
    ? USER_TERMS_LINKS_UPDATED
    : USER_TERMS_LINKS_OLD;
};

/**
 * Returns privacy statement PDF links for the /privacy-statement page.
 * @param isGmFleetRebrandEnabled - When true, returns GM Fleet rebrand documents
 * @param isOnStarFleetIntelligenceRebrandEnabled - When true, returns OnStar Fleet Intelligence documents
 */
export const getUserPrivacyLinks = (
  isGmFleetRebrandEnabled: boolean,
  isOnStarFleetIntelligenceRebrandEnabled = false
) => {
  if (isOnStarFleetIntelligenceRebrandEnabled) {
    return buildOnStarFleetIntelligencePrivacyLinks();
  }

  return isGmFleetRebrandEnabled
    ? USER_PRIVACY_LINKS_UPDATED
    : USER_PRIVACY_LINKS_OLD;
};

/**
 * Returns the user-terms URL used in onboarding and checkout flows.
 * OnStar Fleet Intelligence documents take precedence when that flag is on;
 * otherwise GM Fleet rebrand when `isGmFleetRebrandEnabled` is true; otherwise legacy.
 * @param isGmFleetRebrandEnabled - When true, returns GM Fleet rebrand documents
 * @param isOnStarFleetIntelligenceRebrandEnabled - When true, returns OnStar Fleet Intelligence documents
 */
export const getTermsAndConditionsUrl = (
  isGmFleetRebrandEnabled: boolean,
  isOnStarFleetIntelligenceRebrandEnabled = false
): string =>
  getLegalPageTerms(
    isGmFleetRebrandEnabled,
    isOnStarFleetIntelligenceRebrandEnabled
  ).find((term) => term.key === 'user-terms')?.link || '';

/**
 * Returns the privacy statement URL used in checkout and quick pay flows.
 * @param isGmFleetRebrandEnabled - When true, returns GM Fleet rebrand documents
 * @param isOnStarFleetIntelligenceRebrandEnabled - When true, returns OnStar Fleet Intelligence documents
 */
export const getPrivacyStatementUrl = (
  isGmFleetRebrandEnabled: boolean,
  isOnStarFleetIntelligenceRebrandEnabled = false
): string =>
  getLegalPageTerms(
    isGmFleetRebrandEnabled,
    isOnStarFleetIntelligenceRebrandEnabled
  ).find((term) => term.key === 'privacy-statement')?.link || '';

/**
 * Returns the drive block terms URL used in remote enable/disable flows.
 * @param isGmFleetRebrandEnabled - When true, returns GM Fleet rebrand documents
 * @param isOnStarFleetIntelligenceRebrandEnabled - When true, returns OnStar Fleet Intelligence documents
 */
export const getDriveBlockTermsUrl = (
  isGmFleetRebrandEnabled: boolean,
  isOnStarFleetIntelligenceRebrandEnabled = false
): string =>
  getLegalPageTerms(
    isGmFleetRebrandEnabled,
    isOnStarFleetIntelligenceRebrandEnabled
  ).find((term) => term.key === 'rve-d-beta-terms')?.link || '';

/**
 * Returns the DualCam terms URL used in DualCam enrollment flows.
 * @param isGmFleetRebrandEnabled - When true, returns GM Fleet rebrand documents
 * @param isOnStarFleetIntelligenceRebrandEnabled - When true, returns OnStar Fleet Intelligence documents
 */
export const getDualCamTermsUrl = (
  isGmFleetRebrandEnabled: boolean,
  isOnStarFleetIntelligenceRebrandEnabled = false
): string =>
  getLegalPageTerms(
    isGmFleetRebrandEnabled,
    isOnStarFleetIntelligenceRebrandEnabled
  ).find((term) => term.key === 'dualcam-terms')?.link || '';
